import { useState } from 'react';
import { useCurrencyClaims } from '@/containers/BankAccounts/useCurrencyClaims';
import { logEvent } from '@/utils/analytics';

import { ANALYTICS_EVENTS } from './analyticsEvents';

const createEmptyBankAccount = (extraValues = {}) => {
  const key = `created-${Math.random()}`;
  return {
    [key]: {
      key: key,
      id: null,
      currency: '',
      accountHolder: '',
      accountNumber: '',
      bic: '',
      routingNumber: '',
      ...extraValues,
    },
  };
};

export const useBankAccounts = (
  initialBankAccounts,
  {
    autoDefaultAccountKey,
    initialDefaultAccountKey,
    initialCurrencyClaims,
    currencies,
    maxAccounts = null,
  },
) => {
  const [accounts, setAccounts] = useState(initialBankAccounts);

  const [defaultAccountKey, setDefaultAccountKey] = useState(
    initialDefaultAccountKey ?? autoDefaultAccountKey,
  );
  const { getCurrencies, claimCurrency, releaseClaim } = useCurrencyClaims(
    currencies,
    initialCurrencyClaims,
  );

  // There can only be one account per currency **at maximum** or up to `maxEntries` if provided
  const canCreate = () => Object.keys(accounts).length < (maxAccounts ?? currencies.length);

  const create = extraValues =>
    setAccounts(prev => ({
      ...prev,
      ...createEmptyBankAccount(extraValues),
    }));

  const setFieldValue = (key, field, value) =>
    setAccounts(prevAccounts => ({
      ...prevAccounts,
      [key]: {
        ...prevAccounts[key],
        [field]: value,
      },
    }));

  const trySetAutoDefaultAccountAsDefault = () => {
    // We can only auto select autoDefault bank account if we have it provided
    // This can be undefined if only bank accounts were submitted but not the application
    // before defaultBankAccounts feature was introduced and they are trying to add more bank
    // accounts and are unselecting the current default they selected before
    if (autoDefaultAccountKey) {
      setDefaultAccountKey(autoDefaultAccountKey);
    } else {
      setDefaultAccountKey(undefined);
    }
  };

  const remove = accountKey =>
    setAccounts(prevAccounts => {
      const { [accountKey]: accountToRemove, ...accountsToKeep } = prevAccounts;
      if (accountKey === defaultAccountKey) {
        trySetAutoDefaultAccountAsDefault();
      }
      releaseClaim(accountKey);

      return accountsToKeep;
    });

  const update = (accountKey, field, value) => {
    const account = accounts[accountKey];
    if (field === 'currency') {
      if (account.currency) {
        releaseClaim(accountKey);
      }
      claimCurrency(value, accountKey);
    }

    if (field === 'useAsDefault') {
      logEvent(ANALYTICS_EVENTS.defaultBankAccount);
      if (value === true) {
        setDefaultAccountKey(accountKey);
      } else {
        trySetAutoDefaultAccountAsDefault();
      }

      return;
    }

    return setFieldValue(accountKey, field, value);
  };

  return {
    accounts,
    defaultAccountKey,
    canCreate,
    currenciesAvailableFor: getCurrencies,
    create,
    update,
    remove,
  };
};
